import React, { useState, useMemo } from "react";
import {
  Button,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Table,
  Modal,
  ModalBody,
} from "reactstrap";
import Select from "components/Select";
import StatesInput from "../StatesInput";
import { useCreateSalesDocument, useUpdateSalesDocument } from "../../api/channels/service";
import { useGetDocuments } from "../../api/documents/service";
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { FaPlus, FaEye } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { getActiveColorStyle } from 'utils/colorUtils';
import { getTheme2FormPageDarkSelectStyles } from 'utils/theme2MultiSelect';
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from 'utils/sidebarShellTheme';

const SalesDocuments = ({
  formData,
  setFormData,
  activeColorStyle: activeColorStyleProp,
  salesLocationId,
  state,
  activeColor,
  isTheme2 = false,
  isLightTheme = false,
}) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(
    () => activeColorStyleProp || getActiveColorStyle(activeColor),
    [activeColorStyleProp, activeColor]
  );
  const selectStyles = useMemo(
    () => getTheme2FormPageDarkSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const inputStyle = useMemo(
    () => (isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined),
    [isTheme2]
  );
  const createSalesDocumentMutation = useCreateSalesDocument();
  const { data: documents } = useGetDocuments();
  const updateSalesDocumentMutation = useUpdateSalesDocument();

  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [currentDocument, setCurrentDocument] = useState({
    package_type: "",
    state: null,
    package_name: "",
    package_description: "",
    default: false,
    active: true,
    description: "",
  });
  const [editingDocumentIndex, setEditingDocumentIndex] = useState(null);
  const [documentsFormIndex, setDocumentsFormIndex] = useState(null);
  const [expandedDocumentsIndex, setExpandedDocumentsIndex] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteIndex, setSelectedDeleteIndex] = useState(null);
  const [documentSearch, setDocumentSearch] = useState("");
  const [viewingPdfUrl, setViewingPdfUrl] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({
    funded: false,
    optional: false,
    dmv: false,
    valuation: false,
  });
  // Local state for documents selection (per package index)
  const [localDocumentsSelection, setLocalDocumentsSelection] = useState({});

  // Theme-based colors (isLightTheme: true = light theme, false = dark theme)
  const getBackgroundColor = () => {
    return isLightTheme ? "rgba(0, 0, 0, 0.03)" : "rgba(255, 255, 255, 0.05)";
  };

  const getBorderColor = () => {
    return isLightTheme ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)";
  };

  const getCategoryBgColor = () => {
    return isLightTheme ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.1)";
  };

  const getTextColor = () => {
    return isLightTheme ? "rgba(0, 0, 0, 0.6)" : "rgba(255, 255, 255, 0.6)";
  };

  const getMutedTextColor = () => {
    return isLightTheme ? "rgba(0, 0, 0, 0.4)" : "rgba(255, 255, 255, 0.4)";
  };

  // Package type options
  const packageTypeOptions = [
    { value: "bhph", label: "BHPH/Scratchpad" },
    { value: "wholesale", label: "Wholesale" },
    { value: "cash", label: "Cash" },
    // { value: "other", label: "Other/Custom" },
  ];

  const handleAddDocument = () => {
    let updatedDocuments;

    if (editingDocumentIndex !== null) {
      // Update mode
      const documentToUpdate = (formData || [])[editingDocumentIndex];
      const docId = documentToUpdate?.doc_id;

      updatedDocuments = [...(formData || [])];
      // Preserve doc_id when updating
      updatedDocuments[editingDocumentIndex] = {
        ...updatedDocuments[editingDocumentIndex],
        ...currentDocument,
        name: currentDocument.package_name,
        description: currentDocument.package_description,
      };
      setFormData(updatedDocuments);
      setEditingDocumentIndex(null);

      // Update API
      if (docId) {
        updateSalesDocumentMutation.mutate({
          id: docId,
          data: {
            active: currentDocument.active,
            package_type: currentDocument.package_type,
            state: currentDocument.state,
            name: currentDocument.package_name,
            description: currentDocument.package_description,
            default: currentDocument.default,
            channel_id: salesLocationId,
          },
        });
      }
    } else {
      // Create mode
      updatedDocuments = [
        ...(formData || []),
        currentDocument,
      ];
      setFormData(updatedDocuments);

      // Create API
      if (salesLocationId) {
        createSalesDocumentMutation.mutate({
          active: currentDocument.active,
          package_type: currentDocument.package_type,
          state: currentDocument.state,
          name: currentDocument.package_name,
          description: currentDocument.package_description,
          default: currentDocument.default,
          channel_id: salesLocationId,
        });
      }
    }

    setCurrentDocument({
      package_type: "",
      state: null,
      package_name: "",
      package_description: "",
      default: false,
      active: true,
      description: "",
    });
    setShowDocumentForm(false);
  };

  const handleEditDocument = (index) => {
    if (editingDocumentIndex === index) {
      // If clicking on same row, close the edit form
      setEditingDocumentIndex(null);
      setCurrentDocument({
        package_type: "",
        state: null,
        package_name: "",
        package_description: "",
        default: false,
        active: true,
        description: "",
      });
    } else {
      // Open edit form for clicked row
      const documentToEdit = (formData || [])[index];
      setCurrentDocument({
        package_type: documentToEdit?.package_type || "",
        state: documentToEdit?.state || null,
        package_name: documentToEdit?.name || "",
        package_description: documentToEdit?.description || "",
        default: documentToEdit?.default || false,
        active: documentToEdit?.active !== undefined ? documentToEdit.active : true,
        description: documentToEdit?.description || "",
      });
      setEditingDocumentIndex(index);
    }
    setShowDocumentForm(false); // Close top form if open
    setExpandedDocumentsIndex(null); // Close expanded documents table
    setDocumentsFormIndex(null); // Close documents form if open
  };

  const handleDeleteClick = (index) => {
    setSelectedDeleteIndex(index);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedDeleteIndex !== null) {
      const deletedDocument = (formData || [])[
        selectedDeleteIndex
      ];
      const updatedDocuments = (formData || []).filter(
        (_, i) => i !== selectedDeleteIndex
      );
      setFormData((prev) => ({ ...prev, sales_documents: updatedDocuments }));

      // Update API immediately - send the deleted document object with is_deleted flag
      if (salesLocationId && deletedDocument) {
        const apiDeleteData = {
          ...deletedDocument,
          is_deleted: true,
        };
        const updateData = {
          sales_documents: apiDeleteData,
        };
        createSalesDocumentMutation.mutate({
          data: updateData,
        });
      }

      setDeleteModalOpen(false);
      setSelectedDeleteIndex(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteIndex(null);
  };

  const handleCancelDocument = () => {
    setCurrentDocument({
      package_type: "",
      state: null,
      package_name: "",
      package_description: "",
      default: false,
      active: true,
      description: "",
    });
    setEditingDocumentIndex(null);
    setShowDocumentForm(false);
  };

  const handleDocumentsClick = (index) => {
    if (documentsFormIndex === index) {
      // If clicking on same row, close the documents form
      setDocumentsFormIndex(null);
    } else {
      // Open documents form for clicked row
      setDocumentsFormIndex(index);

      // Always sync with formData documents (if they exist, they are selected)
      const packageToUpdate = (formData || [])[index];
      const currentDocuments = packageToUpdate?.documents || [];

      // Update local documents selection from formData
      setLocalDocumentsSelection((prev) => ({
        ...prev,
        [index]: [...currentDocuments],
      }));
    }
    setEditingDocumentIndex(null); // Close any open edit form
    setShowDocumentForm(false); // Close top form if open
    setExpandedDocumentsIndex(null); // Close expanded documents table
  };

  const handleAddDocumentToPackage = (documentItem, packageIndex) => {
    // Get current documents from local state
    const currentDocuments = localDocumentsSelection[packageIndex] || [];

    // Check if document is already added
    // Compare by id, documentId, and nested document.id/document.documentId
    const isAlreadyAdded = currentDocuments.some((doc) => {
      // Top-level id comparison
      if (doc.id && documentItem.id && doc.id === documentItem.id) return true;

      // Top-level documentId comparison
      if (doc.documentId && documentItem.documentId && doc.documentId === documentItem.documentId) return true;

      // Nested document.id comparison
      if (doc.document?.id && documentItem.document?.id && doc.document.id === documentItem.document.id) return true;

      // Nested document.documentId comparison
      if (doc.document?.documentId && documentItem.document?.documentId && doc.document.documentId === documentItem.document.documentId) return true;

      // Cross-comparisons
      if (doc.id && documentItem.document?.id && doc.id === documentItem.document.id) return true;
      if (doc.documentId && documentItem.document?.documentId && doc.documentId === documentItem.document.documentId) return true;
      if (doc.document?.id && documentItem.id && doc.document.id === documentItem.id) return true;
      if (doc.document?.documentId && documentItem.documentId && doc.document.documentId === documentItem.documentId) return true;

      return false;
    });

    if (!isAlreadyAdded) {
      // Update local state only
      setLocalDocumentsSelection((prev) => ({
        ...prev,
        [packageIndex]: [...currentDocuments, documentItem],
      }));
    }
  };

  const handleRemoveDocumentFromPackage = (documentId, packageIndex) => {
    // Get current documents from local state
    const currentDocuments = localDocumentsSelection[packageIndex] || [];

    // Filter out the document to remove
    const updatedDocuments = currentDocuments.filter((doc) => {
      // Remove if matches by id, documentId, or nested document.id/document.documentId
      return (
        doc.id !== documentId &&
        doc.documentId !== documentId &&
        doc.document?.id !== documentId &&
        doc.document?.documentId !== documentId
      );
    });

    // Update local state only
    setLocalDocumentsSelection((prev) => ({
      ...prev,
      [packageIndex]: updatedDocuments,
    }));
  };

  const handleRemoveDocumentFromFormData = (documentId, packageIndex) => {
    // Get current documents from formData
    const packageToUpdate = (formData || [])[packageIndex];
    const currentDocuments = packageToUpdate?.documents || [];
    const docId = packageToUpdate?.doc_id;

    // Filter out the document to remove
    const updatedDocuments = currentDocuments.filter((doc) => {
      // Remove if matches by id, documentId, or nested document.id/document.documentId
      return (
        doc.id !== documentId &&
        doc.documentId !== documentId &&
        doc.document?.id !== documentId &&
        doc.document?.documentId !== documentId
      );
    });

    // Update formData
    const updatedPackages = [...(formData || [])];
    updatedPackages[packageIndex] = {
      ...updatedPackages[packageIndex],
      documents: updatedDocuments,
    };
    setFormData(updatedPackages);

    // Update API if docId exists
    if (docId && salesLocationId) {
      const documentIds = updatedDocuments.map((doc) => doc?.id).filter(Boolean);
      updateSalesDocumentMutation.mutate({
        id: docId,
        data: {
          documents: documentIds,
          channel_id: salesLocationId,
        },
      });
    }
  };

  const handleSaveDocumentPackage = (packageIndex) => {
    const packageToUpdate = (formData || [])[packageIndex];
    const docId = packageToUpdate?.doc_id;

    // Get selected documents from local state
    const selectedDocuments = localDocumentsSelection[packageIndex] || [];

    // Extract document IDs from selected documents
    const documentIds = selectedDocuments.map((doc) => {
      return doc?.id;
    }).filter(Boolean);

    if (docId && salesLocationId) {
      updateSalesDocumentMutation.mutate(
        {
          id: docId,
          data: {
            documents: documentIds,
            channel_id: salesLocationId,
          },
        },
        {
          onSuccess: () => {
            // Update formData with local documents selection after successful save
            const updatedPackages = [...(formData || [])];
            updatedPackages[packageIndex] = {
              ...updatedPackages[packageIndex],
              documents: selectedDocuments,
            };
            setFormData(updatedPackages);

            // Keep form open - don't clear local state or close form
          },
        }
      );
    }
  };

  const getDocumentUrl = (doc) => {
    // Try different possible URL locations
    let url = null;
    if (doc.document?.url) {
      url = doc.document.url;
    } else if (doc.document?.data?.attributes?.url) {
      url = doc.document.data.attributes.url;
    } else if (doc.url) {
      url = doc.url;
    } else if (doc.documentId) {
      // Construct URL from documentId if available
      url = `${process.env.REACT_APP_API_URL || ''}upload/files/${doc.documentId}`;
    }

    // If URL is relative, prepend with REACT_APP_PHOTO_URL
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      url = `${process.env.REACT_APP_PHOTO_URL || ''}${url.startsWith('/') ? url : '/' + url}`;
    }

    return url;
  };

  // PDF Viewer plugin
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const handleViewDocument = (doc, packageIndex = null) => {
    const url = getDocumentUrl(doc);
    if (url) {
      // If viewing from expanded table, don't open documents form
      // Just open PDF - when closed, it will return to expanded table
      // Only open documents form if it's not from expanded table and form is not already open
      if (packageIndex !== null && expandedDocumentsIndex !== packageIndex && documentsFormIndex !== packageIndex) {
        setDocumentsFormIndex(packageIndex);
        // Initialize local documents selection if not exists
        if (!localDocumentsSelection[packageIndex]) {
          const packageToUpdate = (formData || [])[packageIndex];
          const currentDocuments = packageToUpdate?.documents || [];
          setLocalDocumentsSelection((prev) => ({
            ...prev,
            [packageIndex]: [...currentDocuments],
          }));
        }
      }
      setViewingPdfUrl(url);
    }
  };

  const handleClosePdf = () => {
    setViewingPdfUrl(null);
    // If PDF was opened from expanded table, don't close documents form
    // Just close PDF and keep table visible
    // If PDF was opened from documents form, keep documents form open
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Group documents by type
  const groupedDocuments = useMemo(() => {
    const docs = documents?.data || [];
    const grouped = {
      funded: [],
      optional: [],
      dmv: [],
      valuation: [],
    };

    docs.forEach((doc) => {
      const type = doc.type?.toLowerCase();
      if (grouped[type]) {
        grouped[type].push(doc);
      }
    });

    // Filter by search
    if (documentSearch) {
      const searchLower = documentSearch.toLowerCase();
      Object.keys(grouped).forEach((key) => {
        grouped[key] = grouped[key].filter(
          (doc) =>
            doc.name?.toLowerCase().includes(searchLower) ||
            doc.document?.name?.toLowerCase().includes(searchLower)
        );
      });
    }

    return grouped;
  }, [documents, documentSearch]);

  // Categories array for rendering
  const categories = useMemo(() => {
    return [
      { key: 'funded', label: t("channelsAdd.salesDocumentsSection.funded") },
      { key: 'optional', label: t("channelsAdd.salesDocumentsSection.optional") },
      { key: 'dmv', label: t("channelsAdd.salesDocumentsSection.dmv") },
      { key: 'valuation', label: t("channelsAdd.salesDocumentsSection.valuation") },
    ];
  }, [t]);

  return (
    <div className="">
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        {!showDocumentForm && (
          <Button
            size="sm"
            style={{ ...activeColorStyle, color: "white" }}
            onClick={() => {
              setEditingDocumentIndex(null); // Close any open edit form
              setShowDocumentForm(true);
            }}
          >
            {t("channelsAdd.salesDocumentsSection.addNew")}
          </Button>
        )}
      </div>

      {showDocumentForm && (
        <>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label">
                  {t("channelsAdd.salesDocumentsSection.packageType")} <span style={{ color: "red" }}>*</span>
                </Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder={t("channelsAdd.salesDocumentsSection.selectPackageType")}
                  name="package_type"
                  isSearchable
                  closeMenuOnSelect={true}
                  value={packageTypeOptions.find(
                    (opt) => opt.value === currentDocument.package_type
                  )}
                  onChange={(option) =>
                    setCurrentDocument((prev) => ({
                      ...prev,
                      package_type: option?.value || "",
                    }))
                  }
                  options={packageTypeOptions}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label className="form-control-label">{t("channelsAdd.salesDocumentsSection.state")}:</Label>
                <StatesInput
                  isDisabled
                  name="state"
                  value={state}
                  onChange={(stateObject) => {
                    setCurrentDocument((prev) => ({
                      ...prev,
                      state: stateObject
                        ? {
                          id: stateObject.id,
                          abbreviation: stateObject.abbreviation,
                        }
                        : null,
                    }));
                  }}
                  activeColor={activeColor}
                  styles={selectStyles}
                  placeholder={t("channelsAdd.salesDocumentsSection.selectState")}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <FormGroup>
                <Label className="form-control-label">
                  {t("channelsAdd.salesDocumentsSection.packageName")} <span style={{ color: "red" }}>*</span>
                </Label>
                <Input
                  type="text"
                  className="form-control"
                  style={inputStyle}
                  value={currentDocument.package_name}
                  onChange={(e) =>
                    setCurrentDocument((prev) => ({
                      ...prev,
                      package_name: e.target.value,
                    }))
                  }
                  placeholder={t("channelsAdd.salesDocumentsSection.enterPackageName")}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <FormGroup>
                <Label className="form-control-label">
                  Package Description:
                </Label>
                <Input
                  type="textarea"
                  className="form-control"
                  style={inputStyle}
                  rows="3"
                  value={currentDocument.package_description}
                  onChange={(e) =>
                    setCurrentDocument((prev) => ({
                      ...prev,
                      package_description: e.target.value,
                    }))
                  }
                  placeholder={t("channelsAdd.salesDocumentsSection.enterPackageDescription")}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <Row>
                <FormGroup>
                  <Label className="form-control-label" style={{ marginRight: "30px" }}>{t("channelsAdd.salesDocumentsSection.makeDefault")}:</Label>
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    checked={!!currentDocument.default}
                    onChange={(e) =>
                      setCurrentDocument((prev) => ({
                        ...prev,
                        default: e.target.checked,
                      }))
                    }
                  />
                </FormGroup>
                <FormGroup>
                  <Label className="form-control-label" style={{ marginRight: "30px" }}>{t("channelsAdd.salesDocumentsSection.isActive")}:</Label>
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    checked={!!currentDocument.active}
                    onChange={(e) =>
                      setCurrentDocument((prev) => ({
                        ...prev,
                        active: e.target.checked,
                      }))
                    }
                  />
                </FormGroup>
              </Row>
            </Col>
            <Col md="6">
            </Col>
          </Row>
          <Row>
            <Col className="text-right">
              <Button
                color="secondary"
                size="sm"
                onClick={handleCancelDocument}
                style={{ marginRight: "10px" }}
              >
                {t("channelsAdd.salesDocumentsSection.cancel")}
              </Button>
              <Button
                size="sm"
                style={{ ...activeColorStyle, color: "white" }}
                onClick={handleAddDocument}
              >
                {editingDocumentIndex !== null ? t("channelsAdd.salesDocumentsSection.update") : t("channelsAdd.salesDocumentsSection.add")}
              </Button>
            </Col>
          </Row>
        </>
      )}

      {formData?.length === 0 ? (
        <div style={{ textAlign: "center", color: getMutedTextColor() }}>
          {t("channelsAdd.salesDocumentsSection.noSalesDocuments")}
        </div>
      ) : (
        <Table responsive>
          <thead className="text-primary">
            <tr>
              <th className="text-center">{t("channelsAdd.salesDocumentsSection.id")}</th>
              <th className="text-center">{t("channelsAdd.salesDocumentsSection.packageType")}</th>
              <th className="text-center">{t("channelsAdd.salesDocumentsSection.packageName")}</th>
              <th className="text-center">{t("channelsAdd.salesDocumentsSection.active")}</th>
              <th className="text-center">{t("channelsAdd.salesDocumentsSection.default")}</th>
              <th className="text-center">{t("channelsAdd.salesDocumentsSection.documents")}</th>
              <th className="text-center">{t("channelsAdd.salesDocumentsSection.copy")}</th>
              <th className="text-right"></th>
            </tr>
          </thead>
          <tbody>
            {formData?.map((document, idx) => (
              <React.Fragment key={idx}>
                <tr
                  onClick={() => {
                    const newExpandedIndex = expandedDocumentsIndex === idx ? null : idx;
                    setExpandedDocumentsIndex(newExpandedIndex);
                    // Close other open forms when opening table
                    if (newExpandedIndex !== null) {
                      setEditingDocumentIndex(null);
                      setDocumentsFormIndex(null);
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <td className="text-center">
                    {document.doc_id}
                  </td>
                  <td className="text-center">
                    {packageTypeOptions.find(
                      (opt) => opt.value === document?.package_type
                    )?.label || document.package_type}
                  </td>
                  <td className="text-center">{document.name}</td>
                  <td className="text-center">{document.active ? t("channelsAdd.salesDocumentsSection.yes") : t("channelsAdd.salesDocumentsSection.no")}</td>
                  <td className="text-center">{document.default ? t("channelsAdd.salesDocumentsSection.yes") : t("channelsAdd.salesDocumentsSection.no")}</td>
                  <td className="text-center">
                    <Button
                      color="info"
                      size="sm"
                      className="btn-icon btn-link like"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDocumentsClick(idx);
                      }}
                    >
                      <i className="tim-icons icon-single-copy-04" />
                    </Button>
                  </td>
                  <td className="text-center">
                    <Button
                      color="success"
                      size="sm"
                      className="btn-icon btn-link like"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Handle copy action
                      }}
                    >
                      <i className="fa fa-copy" />
                    </Button>
                  </td>
                  <td className="text-right">
                    <div className="actions-right">
                      <Button
                        color="warning"
                        size="sm"
                        className="btn-icon btn-link like"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditDocument(idx);
                        }}
                      >
                        <i className="tim-icons icon-pencil" />
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        className="btn-icon btn-link like"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(idx);
                        }}
                      >
                        <i className="tim-icons icon-simple-remove" />
                      </Button>
                    </div>
                  </td>
                </tr>
                {editingDocumentIndex === idx && (
                  <tr>
                    <td colSpan="8">
                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label className="form-control-label">
                              {t("channelsAdd.salesDocumentsSection.packageType")}{" "}
                              <span style={{ color: "red" }}>*</span>
                            </Label>
                            <Select
                              activeColor={activeColor}
                              styles={selectStyles}
                              className="react-select"
                              placeholder={t("channelsAdd.salesDocumentsSection.selectPackageType")}
                              name="package_type"
                              isSearchable
                              closeMenuOnSelect={true}
                              value={packageTypeOptions.find(
                                (opt) =>
                                  opt.value === currentDocument.package_type
                              )}
                              onChange={(option) =>
                                setCurrentDocument((prev) => ({
                                  ...prev,
                                  package_type: option?.value || "",
                                }))
                              }
                              options={packageTypeOptions}
                            />
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup>
                            <Label className="form-control-label">
                              State:
                            </Label>
                            <StatesInput
                              isDisabled
                              name="state"
                              value={state}
                              onChange={(stateObject) => {
                                setCurrentDocument((prev) => ({
                                  ...prev,
                                  state: stateObject
                                    ? {
                                      id: stateObject.id,
                                      abbreviation:
                                        stateObject.abbreviation,
                                    }
                                    : null,
                                }));
                              }}
                              activeColor={activeColor}
                              styles={selectStyles}
                              placeholder={t("channelsAdd.salesDocumentsSection.selectState")}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="12">
                          <FormGroup>
                            <Label className="form-control-label">
                              {t("channelsAdd.salesDocumentsSection.packageName")}{" "}
                              <span style={{ color: "red" }}>*</span>
                            </Label>
                            <Input
                              type="text"
                              className="form-control"
                              style={inputStyle}
                              value={currentDocument.package_name}
                              onChange={(e) =>
                                setCurrentDocument((prev) => ({
                                  ...prev,
                                  package_name: e.target.value,
                                }))
                              }
                              placeholder={t("channelsAdd.salesDocumentsSection.enterPackageName")}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="12">
                          <FormGroup>
                            <Label className="form-control-label">
                              Package Description:
                            </Label>
                            <Input
                              type="textarea"
                              className="form-control"
                              style={inputStyle}
                              rows="3"
                              value={currentDocument.package_description}
                              onChange={(e) =>
                                setCurrentDocument((prev) => ({
                                  ...prev,
                                  package_description: e.target.value,
                                }))
                              }
                              placeholder={t("channelsAdd.salesDocumentsSection.enterPackageDescription")}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="6">
                          <Row>
                            <FormGroup>
                              <Label className="form-control-label" style={{ marginRight: "30px" }}>
                                {t("channelsAdd.salesDocumentsSection.makeDefault")}:
                              </Label>
                              <Input
                                type="checkbox"
                                className="form-check-input"
                                checked={!!currentDocument.default}
                                onChange={(e) =>
                                  setCurrentDocument((prev) => ({
                                    ...prev,
                                    default: e.target.checked,
                                  }))
                                }
                              />
                            </FormGroup>
                            <FormGroup>
                              <Label className="form-control-label" style={{ marginRight: "30px" }}>
                                {t("channelsAdd.salesDocumentsSection.isActive")}:
                              </Label>
                              <Input
                                type="checkbox"
                                className="form-check-input"
                                checked={!!currentDocument.active}
                                onChange={(e) =>
                                  setCurrentDocument((prev) => ({
                                    ...prev,
                                    active: e.target.checked,
                                  }))
                                }
                              />
                            </FormGroup>
                          </Row>
                        </Col>
                        <Col md="6">
                        </Col>
                      </Row>
                      <Row>
                        <Col className="text-right">
                          <Button
                            color="secondary"
                            size="sm"
                            onClick={handleCancelDocument}
                            style={{ marginRight: "10px" }}
                          >
                            {t("channelsAdd.salesDocumentsSection.cancel")}
                          </Button>
                          <Button
                            size="sm"
                            style={{ ...activeColorStyle, color: "white" }}
                            onClick={handleAddDocument}
                          >
                            {t("channelsAdd.salesDocumentsSection.update")}
                          </Button>
                        </Col>
                      </Row>
                    </td>
                  </tr>
                )}
                {expandedDocumentsIndex === idx && (
                  <tr>
                    <td colSpan="8" style={{ padding: "15px", backgroundColor: getBackgroundColor() }}>
                      {viewingPdfUrl ? (
                        <div style={{
                          padding: "0px",
                          height: "700px",
                          position: "relative",
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden"
                        }}>
                          {/* Header with Close button */}
                          <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            paddingBottom: "10px",
                            flexShrink: 0
                          }}>
                            <Label className="form-control-label" style={{ fontWeight: "bold" }}>
                              {t("channelsAdd.salesDocumentsSection.documentViewer")}
                            </Label>
                            <Button
                              type="button"
                              color="secondary"
                              size="sm"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleClosePdf();
                              }}
                            >
                              <i className="tim-icons icon-simple-remove" style={{ marginRight: "5px" }} />
                              {t("channelsAdd.salesDocumentsSection.close")}
                            </Button>
                          </div>

                          {/* PDF Viewer with all controls built-in */}
                          <div style={{
                            flex: 1,
                            minHeight: 0,
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            borderRadius: "4px",
                            overflow: "hidden",
                            position: "relative"
                          }}>
                            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                              <div style={{ height: "100%", overflow: "auto" }}>
                                <Viewer
                                  defaultScale={1.0}
                                  theme={isLightTheme ? "light" : "dark"}
                                  fileUrl={viewingPdfUrl}
                                  plugins={[defaultLayoutPluginInstance]}
                                />
                              </div>
                            </Worker>
                          </div>
                        </div>
                      ) : (
                        <>
                          {(document.documents || []).length === 0 ? (
                            <div style={{ textAlign: "center", color: getMutedTextColor(), padding: "20px" }}>
                              {t("channelsAdd.salesDocumentsSection.noDocumentsSelected")}
                            </div>
                          ) : (
                            <Table responsive size="sm" style={{ marginBottom: 0 }}>
                              <thead>
                                <tr>
                                  <th style={{ fontSize: 13, fontWeight: "bold" }}>{t("channelsAdd.salesDocumentsSection.id")}</th>
                                  <th style={{ fontSize: 13, fontWeight: "bold" }}>{t("channelsAdd.salesDocumentsSection.documentName")}</th>
                                  <th style={{ fontSize: 13, fontWeight: "bold", width: "100px" }} className="text-center">{t("channelsAdd.salesDocumentsSection.view")}</th>
                                  <th style={{ fontSize: 13, fontWeight: "bold", width: "100px" }} className="text-center">{t("channelsAdd.salesDocumentsSection.remove")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(document.documents || []).map((doc, docIdx) => (
                                  <tr key={docIdx}>
                                    <td style={{ fontSize: 13 }}>
                                      {doc.doc_id}
                                    </td>
                                    <td style={{ fontSize: 13 }}>
                                      {doc.name || doc.document?.name || "Untitled"}
                                    </td>
                                    <td className="text-center">
                                      <button
                                        type="button"
                                        className="btn-icon btn-link like"
                                        style={{ padding: "0px 6px" }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleViewDocument(doc, idx);
                                        }}
                                      >
                                        <FaEye style={{ color: isLightTheme ? "#000" : "#fff" }} />
                                      </button>
                                    </td>
                                    <td className="text-center">
                                      <button
                                        type="button"
                                        className="btn-icon btn-link like"
                                        style={{ padding: "0px 6px" }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          const docIdToRemove = doc.id || doc.documentId || doc.document?.id || doc.document?.documentId;
                                          handleRemoveDocumentFromFormData(docIdToRemove, idx);
                                        }}
                                      >
                                        <MdClose style={{ color: isLightTheme ? "#000" : "#fff" }} />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                )}
                {documentsFormIndex === idx && (
                  <tr>
                    <td colSpan="8" >
                      {!viewingPdfUrl && (
                        <div style={{ marginBottom: "20px" }}>
                          <Label style={{ fontWeight: "bold", display: "block", fontSize: 13 }}>
                            {t("channelsAdd.salesDocumentsSection.selectAllRelatedDealDocuments")}
                          </Label>
                          <p style={{ fontSize: "13px", color: getTextColor(), marginBottom: "15px" }}>
                            {t("channelsAdd.salesDocumentsSection.selectAllRelatedDealDocumentsDescription")}
                          </p>
                        </div>
                      )}
                      {viewingPdfUrl ? (
                        <Col md="12">
                          <div style={{
                            padding: "15px",
                            backgroundColor: getBackgroundColor(),
                            height: "700px",
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden"
                          }}>
                            {/* Header with Close button */}
                            <div style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              paddingBottom: "10px",
                              flexShrink: 0
                            }}>
                              <Label className="form-control-label" style={{ fontWeight: "bold" }}>
                                {t("channelsAdd.salesDocumentsSection.documentViewer")}
                              </Label>
                              <Button
                                type="button"
                                color="secondary"
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleClosePdf();
                                }}
                              >
                                <i className="tim-icons icon-simple-remove" style={{ marginRight: "5px" }} />
                                {t("channelsAdd.salesDocumentsSection.close")}
                              </Button>
                            </div>

                            {/* PDF Viewer with all controls built-in */}
                            <div style={{
                              flex: 1,
                              minHeight: 0,
                              border: "1px solid rgba(255, 255, 255, 0.1)",
                              borderRadius: "4px",
                              overflow: "hidden",
                              position: "relative"
                            }}>
                              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                                <div style={{ height: "100%", overflow: "auto" }}>
                                  <Viewer
                                    defaultScale={1.0}
                                    theme={isLightTheme ? "light" : "dark"}
                                    fileUrl={viewingPdfUrl}
                                    plugins={[defaultLayoutPluginInstance]}
                                  />
                                </div>
                              </Worker>
                            </div>
                          </div>
                        </Col>
                      ) : (
                        <>
                          <Row>
                            <Col md="6">
                              <div style={{ border: `1px solid ${getBorderColor()}`, padding: "15px", backgroundColor: getBackgroundColor(), height: "500px", overflowY: "auto" }}>
                                <FormGroup style={{ marginBottom: "15px" }}>
                                  <Input
                                    type="text"
                                    className="form-control"
                                    style={inputStyle}
                                    placeholder="Search..."
                                    value={documentSearch || ""}
                                    onChange={(e) => setDocumentSearch(e.target.value || "")}
                                  />
                                </FormGroup>
                                <Label style={{ marginBottom: "10px", display: "block", fontWeight: "bold", fontSize: 13 }}>
                                  {t("channelsAdd.salesDocumentsSection.availableDocuments")}
                                </Label>
                                <div style={{ borderBottom: `1px solid ${getBorderColor()}`, marginBottom: "10px", paddingBottom: "5px" }}>
                                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <span style={{ fontWeight: "bold", fontSize: 13 }}>{t("channelsAdd.salesDocumentsSection.addView")}</span>
                                    <span style={{ fontWeight: "bold", fontSize: 13 }}>{t("channelsAdd.salesDocumentsSection.documentName")}</span>
                                  </div>
                                </div>

                                {/* Categories */}
                                {categories.map((category) => {
                                  const categoryDocs = groupedDocuments[category.key] || [];
                                  const isExpanded = expandedCategories[category.key];

                                  return (
                                    <div key={category.key} style={{ marginBottom: "10px" }}>
                                      <div
                                        onClick={() => toggleCategory(category.key)}
                                        style={{
                                          cursor: "pointer",
                                          padding: "8px",
                                          backgroundColor: getCategoryBgColor(),
                                          fontWeight: "bold",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          marginBottom: "5px",
                                        }}
                                        className="form-control-label"
                                      >
                                        <span>{category.label}</span>
                                        <i
                                          className={`fa fa-chevron-${isExpanded ? "down" : "right"}`}
                                          style={{ marginLeft: "10px" }}
                                        />
                                      </div>
                                      {isExpanded && (
                                        <div style={{ padding: "5px 0" }}>
                                          {categoryDocs.length === 0 ? (
                                            <div style={{ padding: "10px", color: getMutedTextColor() }} className="form-control-label">{t("channelsAdd.salesDocumentsSection.noDocuments")}</div>
                                          ) : (
                                            categoryDocs.map((doc) => (
                                              <div
                                                key={doc.id}
                                                style={{
                                                  padding: "8px",
                                                  borderBottom: `1px solid ${getBorderColor()}`,
                                                  display: "flex",
                                                  justifyContent: "space-between",
                                                  alignItems: "center",
                                                }}
                                              >
                                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0px" }}>
                                                  <button
                                                    type="info"
                                                    size="sm"
                                                    style={{ padding: "0px 6px" }}
                                                    className="btn-icon btn-link like"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      handleAddDocumentToPackage(doc, idx);
                                                    }}
                                                  >
                                                    <FaPlus style={{ color: isLightTheme ? "#000" : "#fff" }} />
                                                  </button>
                                                  <button
                                                    type="info"
                                                    size="sm"
                                                    style={{ padding: "0px 6px" }}
                                                    className="btn-icon btn-link like"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      handleViewDocument(doc);
                                                    }}
                                                  >
                                                    <FaEye style={{ color: isLightTheme ? "#000" : "#fff" }} />
                                                  </button>
                                                </div>
                                                <span style={{ fontSize: 13, flex: 1, marginLeft: 10 }}>
                                                  {doc.name || doc.document?.name || "Untitled"}
                                                </span>
                                              </div>
                                            ))
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </Col>
                            <Col md="6">
                              <div style={{ border: `1px solid ${getBorderColor()}`, padding: "15px", backgroundColor: getBackgroundColor(), height: "500px", overflowY: "auto" }}>
                                <Label style={{ marginBottom: "15px", display: "block", fontWeight: "bold", fontSize: 13 }}>
                                  {t("channelsAdd.salesDocumentsSection.documentsBelowHaveBeenSelected")}
                                </Label>
                                {(localDocumentsSelection[idx] || []).length === 0 ? (
                                  <div style={{ textAlign: "center", color: getMutedTextColor() }} className="form-control-label">
                                    {t("channelsAdd.salesDocumentsSection.noDocumentsSelected")}
                                  </div>
                                ) : (
                                  <div>
                                    {(localDocumentsSelection[idx] || []).map((selectedDoc, docIdx) => (
                                      <div
                                        key={docIdx}
                                        style={{
                                          padding: "10px",
                                          borderBottom: `1px solid ${getBorderColor()}`,
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                        }}
                                      >
                                        <span style={{ flex: 1, fontSize: 13 }}>
                                          {selectedDoc.name || selectedDoc.document?.name || "Untitled"}
                                        </span>
                                        <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                                          <button
                                            type="button"
                                            size="sm"
                                            className="btn-icon btn-link like"
                                            style={{ padding: "0px 6px" }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleViewDocument(selectedDoc);
                                            }}
                                          >
                                            <FaEye style={{ color: isLightTheme ? "#000" : "#fff" }} />
                                          </button>
                                          <button
                                            type="info"
                                            size="sm"
                                            className="btn-icon btn-link like"
                                            style={{ padding: "0px 6px" }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleRemoveDocumentFromPackage(
                                                selectedDoc.document?.id ||
                                                selectedDoc.document?.documentId ||
                                                selectedDoc.id ||
                                                selectedDoc.documentId,
                                                idx
                                              );
                                            }}
                                          >
                                            <MdClose style={{ color: isLightTheme ? "#000" : "#fff" }} />
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </Col>
                          </Row>
                        </>
                      )}
                      {!viewingPdfUrl && (
                        <Row style={{ marginTop: "15px" }}>
                          <Col>
                            <div style={{ padding: "10px", backgroundColor: "rgba(255, 193, 7, 0.2)", marginBottom: "10px", border: "1px solid rgba(255, 193, 7, 0.3)" }}>
                              <span style={{ fontWeight: "bold", fontSize: 13 }}>{t("channelsAdd.salesDocumentsSection.notice")}</span>{" "}
                              <span style={{ fontSize: 13 }}>{t("channelsAdd.salesDocumentsSection.noticeText")}</span>
                            </div>
                            <div className="text-right">
                              <Button
                                type="button"
                                color="secondary"
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  // Clear local selection for this package
                                  setLocalDocumentsSelection((prev) => {
                                    const newState = { ...prev };
                                    delete newState[idx];
                                    return newState;
                                  });
                                  setDocumentsFormIndex(null);
                                }}
                                style={{ marginRight: "10px" }}
                              >
                                {t("channelsAdd.salesDocumentsSection.cancel")}
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                style={{ ...activeColorStyle, color: "white" }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSaveDocumentPackage(idx);
                                }}
                                disabled={updateSalesDocumentMutation.isPending}
                              >
                                {updateSalesDocumentMutation.isPending ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm mr-2"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                    {t("channelsAdd.salesDocumentsSection.saving")}
                                  </>
                                ) : (
                                  t("channelsAdd.salesDocumentsSection.saveDocumentPackage")
                                )}
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        toggle={handleCancelDelete}
        centered={false}
        style={{ marginTop: "100px" }}
      >
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">Delete Sales Document</h6>
        </div>
        <ModalBody className="text-center">
          <p>Are you sure you want to delete this sales document?</p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelDelete}
            disabled={createSalesDocumentMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            style={{
              background:
                "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              backgroundImage:
                "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              borderColor: "#fd5d93",
              color: "white",
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={createSalesDocumentMutation.isPending}
          >
            {createSalesDocumentMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Deleting...
              </>
            ) : (
              "Yes, Delete"
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default SalesDocuments;
